import React, { useState, useEffect, useRef } from "react";
import { NavLink, useLocation, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { mediaUrl } from "@/lib/upload";
import { playNotify, playBoomAlarm } from "@/lib/sound";
import { toast } from "sonner";
import api from "@/lib/api";
import { cn } from "@/lib/utils";
import { BreakProvider } from "@/context/BreakContext";
import { BreakMenu, BreakOverlay } from "@/components/BreakTools";
import { TeamStatus } from "@/components/TeamStatus";
import {
  SquaresFour, Users, Truck, UsersThree, ClipboardText, Megaphone, ChatsCircle,
  NotePencil, BellRinging, CurrencyDollar, Receipt, Prohibit, ChartBar, Kanban,
  Pulse, CloudSun, SignOut, List, X, MapTrifold, SteeringWheel, ShieldCheck, SealCheck, UserMinus,
  CaretDown, Truck as TruckLogo, MagnifyingGlass, UserPlus, ChartLineUp, Trophy,
  Gear, Sun, Moon, Buildings, Timer, Binoculars, MapPinLine, IdentificationBadge, Path, ArrowsLeftRight, HandCoins, Coins, Wallet, Van, Robot, Newspaper, ListChecks, Calculator, Headset,
} from "@phosphor-icons/react";
import { ShiftGate, ShiftButton } from "@/components/ShiftGate";
import { DispatchCheckGate } from "@/components/DispatchCheckGate";
import { DdclReminder } from "@/components/DdclReminder";

const SECTIONS = [
  {
    label: "Overview",
    items: [
      { to: "/", label: "Dashboard", icon: SquaresFour, roles: ["admin", "sales", "dispatcher"] },
      { to: "/freight-news", label: "Freight News", icon: Newspaper, roles: ["admin", "sales", "dispatcher", "driver"] },
      { to: "/cpm-calculator", label: "CPM Calculator", icon: Calculator, roles: ["admin", "sales", "dispatcher"] },
      { to: "/marketing", label: "Multichannel Marketing", icon: Headset, roles: ["admin", "sales", "dispatcher"] },
    ],
  },
  {
    label: "Operations",
    items: [
      { to: "/my-loads", label: "My Loads", icon: Truck, roles: ["driver"] },
      { to: "/carrier-portal", label: "My Company", icon: Truck, roles: ["carrier"] },
      { to: "/dispatch", label: "Dispatch Board", icon: Kanban, roles: ["admin", "dispatcher"] },
      { to: "/loads", label: "Load Management", icon: Truck, roles: ["admin", "dispatcher"] },
      { to: "/asterism-ai", label: "Asterism AI", icon: Robot, roles: ["admin", "dispatcher"] },
      { to: "/analytics", label: "Asterism Analytics", icon: ChartLineUp, roles: ["admin", "sales", "dispatcher"] },
      { to: "/route-planning", label: "Carrier Status", icon: Path, roles: ["admin", "dispatcher"] },
      { to: "/driver-info", label: "Driver Info", icon: SteeringWheel, roles: ["admin", "dispatcher"] },
      { to: "/fleet-map", label: "Fleet Map", icon: MapTrifold, roles: ["admin", "dispatcher"] },
      { to: "/tasks", label: "Tasks", icon: ClipboardText, roles: ["admin", "dispatcher"] },
      { to: "/tasks", label: "Tasks Management", icon: ClipboardText, roles: ["sales"] },
    ],
  },
  {
    label: "Dispatcher Desk",
    items: [
      { to: "/ddcl", label: "DDCL Check List", icon: ListChecks, roles: ["admin", "dispatcher"] },
      { to: "/earnings", label: "My Earnings", icon: CurrencyDollar, roles: ["dispatcher"] },
      { to: "/earnings", label: "Dispatcher Earnings", icon: CurrencyDollar, roles: ["admin"] },
    ],
  },
  {
    label: "Fleet",
    items: [
      { to: "/fleet", label: "Trucks & Trailers", icon: Truck, roles: ["admin", "dispatcher"] },
      { to: "/drivers", label: "Drivers & Pay", icon: SteeringWheel, roles: ["admin", "dispatcher"] },
      { to: "/safety", label: "Safety & IFTA", icon: ShieldCheck, roles: ["admin", "dispatcher"] },
    ],
  },
  {
    label: "Sales / CRM",
    items: [
      { to: "/leads", label: "Lead Management", icon: Users, roles: ["admin", "sales"] },
      { to: "/carriers", label: "Active Carriers", icon: Buildings, roles: ["admin"] },
      { to: "/submit-lead", label: "Submit Lead", icon: UserPlus, roles: ["admin", "sales"] },
      { to: "/rejected-leads", label: "Rejected Leads", icon: Prohibit, roles: ["admin", "sales"] },
      { to: "/carrier-authorization", label: "Carrier Authorization", icon: SealCheck, roles: ["admin"] },
      { to: "/offboard-carriers", label: "OffBoard Carriers", icon: UserMinus, roles: ["admin"] },
      { to: "/my-activity", label: "My Activity", icon: ChartLineUp, roles: ["sales", "dispatcher"] },
      { to: "/leaderboard", label: "Leaderboard", icon: Trophy, roles: ["admin", "sales"] },
    ],
  },
  {
    label: "Accounting",
    items: [
      { to: "/invoices", label: "Invoices", icon: Receipt, roles: ["admin"] },
      { to: "/receivable", label: "Accounts Receivable", icon: Coins, roles: ["admin", "dispatcher"] },
      { to: "/trailer-renting", label: "Trailer Renting", icon: Van, roles: ["admin"] },
      { to: "/operating-costs", label: "Monthly Operating Cost", icon: Wallet, roles: ["admin"] },
      { to: "/payable", label: "Accounts Payable", icon: HandCoins, roles: ["admin"] },
      { to: "/carrier-brokers", label: "Carrier ↔ Broker", icon: ArrowsLeftRight, roles: ["admin", "dispatcher"] },
      { to: "/brokers", label: "Brokers DNU", icon: Prohibit, roles: ["admin", "dispatcher"] },
    ],
  },
  {
    label: "Workspace",
    items: [
      { to: "/announcements", label: "Announcements", icon: Megaphone, roles: ["admin", "sales", "dispatcher"] },
      { to: "/groups", label: "Communication Hub", icon: ChatsCircle, roles: ["admin", "dispatcher", "sales"] },
      { to: "/notes", label: "Working Notes", icon: NotePencil, roles: ["admin", "sales", "dispatcher"] },
      { to: "/alerts", label: "Alerts", icon: BellRinging, roles: ["admin", "dispatcher", "driver"] },
      { to: "/activity", label: "Activity", icon: Pulse, roles: ["admin", "dispatcher"] },
      { to: "/weather", label: "Weather", icon: CloudSun, roles: ["admin", "dispatcher", "driver"] },
      { to: "/team", label: "Team (Users)", icon: UsersThree, roles: ["admin"] },
      { to: "/hr", label: "HR Operations", icon: IdentificationBadge, roles: ["admin"] },
      { to: "/break-tracker", label: "Break Tracker", icon: Timer, roles: ["admin"] },
      { to: "/settings", label: "Settings", icon: Gear, roles: ["admin", "sales", "dispatcher", "driver", "carrier"] },
    ],
  },
];

const ROLE_LABELS = { admin: "Administrator", sales: "Sales Agent", dispatcher: "Dispatcher", driver: "Driver" };

export default function Layout() {
  const { user, logout } = useAuth();
  const { dark, setDark } = useTheme();
  const toggle = () => { const v = !dark; setDark(v); api.put("/profile", { dark_mode: v }).catch((e) => console.error("Failed to save theme", e)); };
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [msgUnread, setMsgUnread] = useState(0);
  const seenAlerts = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => { setOpen(false); }, [location.pathname]);

  useEffect(() => {
    let active = true;
    const poll = async () => {
      try {
        const { data } = await api.get("/alerts");
        if (!active) return;
        const un = data.filter((a) => !a.read);
        const isMsg = (a) => String(a.title || "").startsWith("New message");
        setMsgUnread(un.filter(isMsg).length);
        setUnread(un.filter((a) => !isMsg(a)).length);
        // Pop a toast + sound for genuinely new alerts (skip the very first load)
        if (seenAlerts.current === null) {
          seenAlerts.current = new Set(data.map((a) => a.id));
        } else {
          const fresh = data.filter((a) => !a.read && !seenAlerts.current.has(a.id));
          fresh.forEach((a) => {
            toast(a.title || "Notification", { description: a.message || "", duration: 6000 });
          });
          const task = fresh.find((a) => String(a.title || "").startsWith("New task assigned"));
          if (task) {
            playBoomAlarm(10);
            if (navigator.vibrate) navigator.vibrate(Array.from({ length: 20 }, (_, i) => (i % 2 ? 200 : 300)));
            document.body.classList.add("task-quake");
            setTimeout(() => document.body.classList.remove("task-quake"), 10000);
          } else if (fresh.length > 0) playNotify();
          data.forEach((a) => seenAlerts.current.add(a.id));
        }
      } catch (e) {}
    };
    poll();
    const id = setInterval(poll, 8000);
    return () => { active = false; clearInterval(id); };
  }, []);

  if (!user) return null;

  const handleLogout = async () => { await logout(); navigate("/login"); };

  const SidebarInner = (
    <div className="flex flex-col h-full">
      <div className="h-16 flex items-center gap-2.5 px-5 border-b border-slate-200 shrink-0">
        <img src="/freightx-logo.webp" alt="Asterism Services" className="w-9 h-9 object-contain" />
        <div>
          <p className="font-heading font-extrabold text-slate-900 leading-none">Asterism Services</p>
          <p className="text-[10px] tracking-wide text-blue-500 mt-0.5 font-semibold">Built with Love! ❤</p>
        </div>
      </div>
      {["admin", "sales", "dispatcher"].includes(user.role) && (
        <div className="px-3 pt-3 shrink-0 space-y-1">
          <NavLink
            to="/"
            end
            data-testid="nav-dashboard-pinned"
            className={({ isActive }) =>
              cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition-colors duration-150",
                isActive ? "bg-blue-600 text-white shadow-sm" : "text-slate-700 hover:text-slate-900 hover:bg-slate-100"
              )
            }
          >
            <SquaresFour size={18} weight="duotone" />Dashboard
          </NavLink>
          <NavLink to="/carrier-snaps" data-testid="nav-carrier-snaps-pinned"
            className={({ isActive }) => cn("flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition-colors duration-150", isActive ? "bg-blue-600 text-white shadow-sm" : "text-slate-700 hover:text-slate-900 hover:bg-slate-100")}>
            <Binoculars size={18} weight="duotone" />Carrier Snaps
          </NavLink>
          <NavLink to="/carrier-tracking" data-testid="nav-carrier-tracking"
            className={({ isActive }) => cn("flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-semibold transition-colors duration-150", isActive ? "bg-blue-600 text-white shadow-sm" : "text-slate-700 hover:text-slate-900 hover:bg-slate-100")}>
            <MapPinLine size={18} weight="duotone" />Carrier Tracking
          </NavLink>
        </div>
      )}
      <nav className="flex-1 overflow-y-auto py-3 px-3 space-y-5">
        {SECTIONS.map((section) => {
          const items = section.items.filter((n) => n.roles.includes(user.role) && n.to !== "/");
          if (!items.length) return null;
          return (
            <div key={section.label}>
              <p className="px-3 mb-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400">{section.label}</p>
              <div className="space-y-0.5">
                {items.map((it) => (
                  <NavLink
                    key={it.to}
                    to={it.to}
                    end={it.to === "/"}
                    data-testid={`nav-${it.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                    className={({ isActive }) =>
                      cn(
                        "flex items-center justify-between gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-150",
                        isActive ? "bg-blue-600 text-white shadow-sm" : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                      )
                    }
                  >
                    <span className="flex items-center gap-3"><it.icon size={18} weight="duotone" />{it.label}</span>
                    {it.to === "/alerts" && unread > 0 && (
                      <span className="bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full tabular" data-testid="nav-badge-alerts">{unread}</span>
                    )}
                    {it.to === "/groups" && msgUnread > 0 && (
                      <span className="bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full tabular" data-testid="nav-badge-messages">{msgUnread}</span>
                    )}
                  </NavLink>
                ))}
              </div>
            </div>
          );
        })}
      </nav>
      <div className="p-3 border-t border-slate-200 shrink-0">
        <div className="flex items-center gap-3 px-2 py-2">
          <div className="w-9 h-9 rounded-full bg-blue-100 overflow-hidden flex items-center justify-center font-heading font-bold text-blue-700">
            {user.avatar_url ? <img src={mediaUrl(user.avatar_url)} alt="" className="w-full h-full object-cover" /> : (user.name?.[0]?.toUpperCase() || "U")}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-slate-900 truncate">{user.name}</p>
            <p className="text-[11px] text-slate-400 truncate">{ROLE_LABELS[user.role]}</p>
          </div>
          <button data-testid="logout-button" onClick={handleLogout} className="text-slate-400 hover:text-red-500 transition-colors p-1.5" title="Sign out">
            <SignOut size={18} />
          </button>
        </div>
      </div>
    </div>
  );

  const staff = ["admin", "sales", "dispatcher"].includes(user.role);
  const shell = (
    <BreakProvider user={user}>
    <BreakOverlay />
    {user.role === "dispatcher" && <DdclReminder />}
    <div className="flex h-screen bg-slate-50 overflow-hidden">
      <aside className="hidden lg:block w-[260px] bg-white border-r border-slate-200 shrink-0">{SidebarInner}</aside>

      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <aside className="absolute left-0 top-0 h-full w-[260px] bg-white border-r border-slate-200">{SidebarInner}</aside>
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 shrink-0 bg-white/90 backdrop-blur-md border-b border-slate-200 flex items-center justify-between px-4 sm:px-6 sticky top-0 z-30">
          <div className="flex items-center gap-3">
            <button className="lg:hidden text-slate-600" onClick={() => setOpen(true)} data-testid="menu-toggle">
              {open ? <X size={22} /> : <List size={22} />}
            </button>
            <p className="text-sm font-semibold text-slate-700 hidden sm:block">{ROLE_LABELS[user.role]} Workspace</p>
          </div>
          <div className="flex items-center gap-3 sm:gap-4 shrink-0">
            {["admin", "sales", "dispatcher"].includes(user.role) && <><ShiftButton /><TeamStatus /><BreakMenu /></>}
            <button onClick={toggle} className="text-slate-500 hover:text-blue-600 transition-colors" data-testid="header-theme-toggle" title={dark ? "Switch to light" : "Switch to dark"}>
              {dark ? <Sun size={21} weight="duotone" /> : <Moon size={21} weight="duotone" />}
            </button>
            <NavLink to="/alerts" className="relative text-slate-500 hover:text-blue-600 transition-colors" data-testid="header-alerts">
              <BellRinging size={22} weight="duotone" />
              {unread > 0 && (
                <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[9px] font-bold w-4 h-4 rounded-full flex items-center justify-center tabular">
                  {unread > 9 ? "9+" : unread}
                </span>
              )}
            </NavLink>
            <NavLink to="/settings" className="hidden sm:flex items-center gap-2 pl-4 border-l border-slate-200 hover:opacity-80 transition-opacity" data-testid="header-profile">
              <div className="w-8 h-8 rounded-full bg-blue-100 overflow-hidden flex items-center justify-center font-bold text-blue-700 text-sm">
                {user.avatar_url ? <img src={mediaUrl(user.avatar_url)} alt="" className="w-full h-full object-cover" /> : user.name?.[0]?.toUpperCase()}
              </div>
              <span className="text-sm font-medium text-slate-700">{user.name?.split(" ")[0]}</span>
            </NavLink>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
      {user?.role === "dispatcher" && <DispatchCheckGate />}
    </div>
    </BreakProvider>
  );
  return staff ? <ShiftGate user={user} onLogout={handleLogout}>{shell}</ShiftGate> : shell;
}
